import { Box, Button, CircularProgress, Divider, IconButton, Stack, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "../../svg/CloseIcon";
import { useAuth } from "../../hooks/useAuth";
import CustomAvatar from "../../component/CustomAvatar";
import { useEffect, useRef, useState } from "react";
import { createCommentOnComment, createCommentOnPost, updateComment, } from "../../utils/apis/memeverse";
import { CommentType, PostType, UserDetailType } from "./memeverse_types";
import { useQuery } from "@tanstack/react-query";
import { fetchUserInfo } from "../../utils/apis/users";
import { timeAgo } from "../../utils/helper";
import ImageItem from "./imageItem";
import { CommentInput } from "./CommentInput";
import { id } from "date-fns/locale";

interface CreateUpdateCommentProps {
  setCreateUpdateCommentVisible: (val: boolean) => void;
  parentPostOrComment: PostType | CommentType;
  existingComment?: CommentType;
  scrollToComment?: (commentId: string) => void
}

const CreateUpdateComment: React.FC<CreateUpdateCommentProps> = (props) => {
  const { setCreateUpdateCommentVisible, parentPostOrComment, existingComment, scrollToComment} = props;
  const { authUser} = useAuth();
  let oldData = existingComment?.comment[existingComment?.comment.length-1].comment_body || '';
  const [ commentContent, setCommentContent ] = useState<string>(oldData);
  const [ isSaving, setIsSaving ] = useState<boolean>(false);
  // const [ images, setImages ] = useState<(File | string)[]>(existingImages || []);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [commentId, setCommentId] = useState<string>('')
  const commentInputRef = useRef(null);

  // const {data: userData} = useQuery({
  //     queryKey: ['users', parentPostOrComment.user_id],
  //     queryFn: () => fetchUserInfo(parentPostOrComment.user_id),
  //     staleTime: Infinity,
  //   })

  let userData: UserDetailType;

  if ('content' in parentPostOrComment) {
    userData = parentPostOrComment.post_user_details;
  } else {
    userData = parentPostOrComment.user_details;
  }

  const validateMentions = (
    comment: string,
    mentionsData: {[key: string]: UserDetailType}
  ) => {
    let copy = {...mentionsData};

    let removal = [];
    for (let mention of Object.values(mentionsData)) {
      if (!comment.includes(`@${mention.userName}`)) {
        removal.push(mention.userName)
      }
    }

    for (let username of removal) {
      delete copy[username];
    }

    return copy;
  }
  
  const handleSaveComment = async () => {
    const { comment, mentionsData } = commentInputRef.current.getData();

    let validatedMentionsData = validateMentions(comment, mentionsData);
    let mentions = (Object.values(validatedMentionsData))
                      .map((user: UserDetailType) => user.user_id)

    if (comment.length <= 300) {
      if (!existingComment && comment) {
        if ('content' in parentPostOrComment) {
          setIsSaving(true)
          createCommentOnPost(
            authUser.uid,
            parentPostOrComment.docId,
            comment,
            mentions
          )
            .then(async (resp) => {
              if (resp.ok) {
                setCreateUpdateCommentVisible(false);
                if (scrollToComment) {
                  const data = await resp.json();
                  if (data && data.id) {
                    scrollToComment(data.id);
                  }
                }
              }
            })
            .catch((err) => {
              console.log(err)
            })
            .finally(() => {
              setIsSaving(false);
              
            })
        } else if ('comment' in parentPostOrComment) {
          setIsSaving(true)
          createCommentOnComment(
            parentPostOrComment.docId,
            parentPostOrComment.parent_post_id,
            authUser.uid,
            comment,
            mentions
          )

            .then((resp) => {
              if (resp.ok) {
                setCreateUpdateCommentVisible(false);
              }
            })
            .catch((err) => {
              console.log(err)
            })
            .finally(() => {
              setIsSaving(false);
            })
        }
      } else if (existingComment) {
        if (oldData !== comment) {
          // start editing
          setIsSaving(true)
          updateComment(authUser?.uid, existingComment.docId, comment)
            .then((resp) => {
              if (resp.ok) {
                setCreateUpdateCommentVisible(false);
              }
            })
            .catch((err) => {
              console.log(err)
            })
            .finally(() => {
              setIsSaving(false);
            })
        }
      }
    }
  };

  // const removeImage = (index: number) => {
  //   let newImages = images.filter((_ , i) => i !== index);
  //   setImages(newImages);
  // }

  // useEffect(() => {
  //   console.log(images)
  // }, [images])

  const getUpdatedComment = (newComment: string) => {
    setCommentContent(newComment);
  }

  const MobileHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          mt: -1,
          px: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'solid 1px',
          borderColor: theme.palette.divider,
          py: 1
        }}
      >
        <Button
          size='medium'
          onClick={() => setCreateUpdateCommentVisible(false)}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          Cancel
        </Button>
        <Box
          gap={2}
          display='flex'
          alignItems='center'
        >
          <Typography
            variant='body2'
            color={commentContent.length <= 300 ? 'text.secondary' : 'error.main'}
          >
            {`${commentContent.length}/300`}
          </Typography>
          <Button
            variant="contained"
            disabled={commentContent === oldData || isSaving || commentContent.length > 300}
            onClick={() => handleSaveComment()}
            startIcon={isSaving ?
              <CircularProgress size={20} color='inherit'/> :
              <></>
            }
          >
            Reply
          </Button>
        </Box>
      </Box>
    )
  }

  const RegularHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h7" color='text.primary'>
          {existingComment ? 'Edit Reply' : 'Reply'}
        </Typography>
        <IconButton
          onClick={() => setCreateUpdateCommentVisible(false)}
          sx={{
            stroke: (theme) => theme.palette.action.active,
          }}
        >
          <CloseIcon strokeWidth={2} stroke={'inherit'}/>
        </IconButton>
      </Box>
    )
  }

  const ParentPostOrComment = () => {
    let createdAt: string;
    let content: string;
    let images: string[];

    if ('content' in parentPostOrComment) {
      createdAt = parentPostOrComment.content[parentPostOrComment.content.length-1].row_created;
      content = parentPostOrComment.content[parentPostOrComment.content.length-1].content_body;
      images = parentPostOrComment.images;
    } else {
      createdAt = parentPostOrComment.comment[parentPostOrComment.comment.length-1].row_created;
      content = parentPostOrComment.comment[parentPostOrComment.comment.length-1].comment_body;
      images = [];
    }

    return (
      <Stack
        direction="row"
        sx={{
          width: '100%',
          gap: {xs: 1.5, md: 2},
          maxWidth: '100%',
          px: {xs: 1.5, md: 2.5},
          minHeight: '75px',
        }}
      >
        {/* Left column: Avatar and vertical line */}
        <Stack
          direction="column"
          alignItems="center"
          sx={{
            width: '48px'
          }}
        >
          <CustomAvatar src={userData?.profilePicUrl} sx={{ width: 48, height: 48 }} />
          <Box sx={{ flexGrow: 1, borderLeft: '2px solid', borderColor: 'grey.300', mt: 1 }} />
        </Stack>
        {/* Right column: Main content */}
        <Stack
          direction="column"
          sx={{
            flex: 1,
            maxWidth: 'calc(100% - 48px - 12px)',
          }}
        >
          {/* User info row */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent='space-between'
            sx={{
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <Typography variant="h7" color="text.primary" sx={{ fontWeight: 600, whiteSpace: 'nowrap', }}>
                {userData?.fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap',}}>
                @{userData?.userName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap',}}>
                &#x2022;
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap',}}>
                {timeAgo(createdAt)
                }
              </Typography>
            </Box>
          </Stack>
          {/* Description row */}
          <Typography
            variant="body1"
            color="text.primary"
            mb={1.5}
            sx={{
              maxHeight: '25dvh',
              overflowY: 'auto',
            }}
          >
            {content}
          </Typography>
          {/* Image row (16:9 ratio) */}
          <Stack
            display={images?.length ? 'flex' : 'none'}
            direction="column"
            spacing={1}
            sx={{
              width: '100%',
              mb: 1.5,
            }}
          >
            {
              images?.map((image, index) => {
                return (
                  <ImageItem
                    key={`meme-image-${index}`}
                    image={image}
                    alt={"meme image"}
                  />
                )
              })
            }
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return (
    <Box
      className="new-comment"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: {xs: 0, md: 2.5},
        py: {xs: 1, md: 2},
        [theme.breakpoints.down('md')]: {
          height: '100%',
        },
      }}
    >
      { isSmallScreen ? <MobileHeader /> : <RegularHeader /> }

      <ParentPostOrComment />

      <Box
        display='flex'
        width='100%'
        gap={2}
        px={{xs: 1.5, md: 2.5}}
        flexGrow={1}
      >
        <CustomAvatar src={authUser?.profilePicUrl} sx={{ width: 48, height: 48 }} />
        <Box
          display='flex'
          flexDirection='column'
          width='100%'
        >
          <CommentInput ref={commentInputRef} defaultComment={oldData} onChange={getUpdatedComment}/>
        </Box>
      </Box>

      <Divider />

      <Box
        display='flex'
        width='100%'
        justifyContent={'flex-end'}
        alignItems={'center'}
        sx={{
          px: {xs: 2, md: 0}
        }}
      >
        <Box
          gap={2}
          alignItems='center'
          sx={{
            display: {xs: 'none', md: 'flex'},
          }}
        >
          <Typography
            variant='body2'
            color={commentContent.length <= 300 ? 'text.secondary' : 'error.main'}
          >
            {`${commentContent.length}/300`}
          </Typography>
          <Button
            variant="contained"
            disabled={commentContent === oldData || isSaving || commentContent.length > 300}
            onClick={() => handleSaveComment()}
            startIcon={isSaving ?
              <CircularProgress size={20} color='inherit'/> :
              <></>
            }
          >
            Comment
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default CreateUpdateComment;
